import { Outlet, createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useMemo } from 'react';
import { z } from 'zod';

import { BranchSheetFromParams } from '@/components/Branches/BranchSheetFromParams';
import { ChatProvider } from '@/components/Chat/ChatContext';
import { BreadcrumbsCodebase } from '@/components/CodebaseView/BreadcrumbsCodebase';
import { CodebaseDocsToolbar } from '@/components/CodebaseView/CodebaseDocsToolbar';
import { CodebaseErrorBanner } from '@/components/CodebaseView/CodebaseErrorBanner';
import { CodebaseHeaderActions } from '@/components/CodebaseView/CodebaseHeaderActions';
import { CodebaseHeaderSkeleton } from '@/components/CodebaseView/CodebaseHeaderSkeleton';
import { CodebaseMainSkeleton } from '@/components/CodebaseView/CodebaseMainSkeleton';
import { CodebaseRightSidebar } from '@/components/CodebaseView/CodebaseRightSidebar';
import { CodebaseRightSidebarTabType } from '@/components/CodebaseView/CodebaseRightSidebarTabType';
import { FlexiblePanelLayout } from '@/components/FlexiblePanelLayout/FlexiblePanelLayout';
import { LeftMainHeader, Tab } from '@/components/FlexiblePanelLayout/LeftMainHeader';
import { LayoutDefault } from '@/components/LayoutDefault';
import { LayoutHeader } from '@/components/LayoutHeader';
import { NotFound404 } from '@/components/NotFound404';
import {
  getSourceAssetVersionNodeIds,
  transformVersionRecordData,
} from '@/components/UserDefinedScope/SourceAssetRecord';
import { UserDefinedScope } from '@/components/UserDefinedScope/UserDefinedScope';
import { LeftSidebarPortal } from '@/components/portals/LeftSidebarPortal';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { CodebaseProvider, useCodebase } from '@/contexts/CodebaseContext';
import { useLayout } from '@/contexts/LayoutContext';
import { MemoryTunerProvider, useMemoryTunerActions } from '@/contexts/MemoryTunerContext';
import { PageProvider } from '@/contexts/PageContext';
import { TabProvider } from '@/contexts/TabContext';
import { useTunerContext } from '@/contexts/TunerContext';
import { VersionGuardProvider } from '@/contexts/VersionGuardContext';
import { VersionsProvider, useVersions } from '@/contexts/VersionsContext';
import { useCodebaseRoute } from '@/hooks/useCodebaseRoute';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { searchParam } from '@/utils/router';

const treeSearchSchema = z.object({
  branchId: searchParam().optional(),
  versionId: searchParam().optional(),
  branch: searchParam().optional(),
  branchCodebase: searchParam().optional(),
});

export const Route = createFileRoute('/$organizationName/cb/$codebaseId')({
  component: ProvidedCodebaseLayout,
  validateSearch: zodValidator(treeSearchSchema),
});

const CodebaseLayout: React.FC = () => {
  const { codebaseId, organizationName, tab, isPage, pathArray } = useCodebaseRoute();
  const { codebase, codebaseName, isPrimaryAssetLoading, isFile } = useCodebase();
  const { branch, branchCodebase, versionId } = Route.useSearch();
  const { hasBranches, isBranchesLoading, selectedBranchId, selectedVersion } = useVersions();
  const { isLargeScreen } = useLayout();

  // 🐉🐉🐉
  // Must use `useNavigate` here instead of `Route.useNavigate` otherwise there seems to be
  // a TanStack Router bug where the `to: '.'` falls back to the route route and causes the Overview page
  // to be rendered instead of the current "tab".
  const navigate = useNavigate();

  const { documentSources, openUserDefinedScope, setOpenUserDefinedScope } = useTunerContext();
  const { setDocumentSources } = useMemoryTunerActions();
  const { value: isBranchesUiEnabled } = useFeatureFlag(FEATURE_FLAGS.BRANCHES_UI);
  const { value: isServeAtlasEnabled } = useFeatureFlag(FEATURE_FLAGS.SERVE_ATLAS);

  const chatSourceVersionNodeIds = useMemo(() => {
    return getSourceAssetVersionNodeIds(documentSources);
  }, [documentSources]);

  const tabSearch = useMemo(() => {
    const branchId = hasBranches ? selectedBranchId : isBranchesLoading ? selectedBranchId : undefined;
    const search = {
      ...(branchId ? { branchId } : {}),
      ...(versionId ? { versionId } : {}),
    };
    return Object.keys(search).length > 0 ? search : undefined;
  }, [hasBranches, isBranchesLoading, selectedBranchId, versionId]);

  const tabs = useMemo<Tab[]>(() => {
    return [
      {
        key: 'overview',
        displayName: 'Overview',
        to: '/$organizationName/cb/$codebaseId/overview/$' as const,
        params: {
          organizationName: organizationName,
          codebaseId: codebaseId,
        },
        search: tabSearch,
      },
      // The Branches tab is gated behind the BRANCHES_UI flag (default-off → hidden).
      ...(isBranchesUiEnabled
        ? [
            {
              key: 'branches',
              displayName: 'Branches',
              to: '/$organizationName/cb/$codebaseId/branches' as const,
              params: {
                organizationName: organizationName,
                codebaseId: codebaseId,
              },
              search: tabSearch,
            },
          ]
        : []),
      {
        key: 'tree',
        displayName: 'Docs',
        to: '/$organizationName/cb/$codebaseId/tree/$' as const,
        params: {
          organizationName: organizationName,
          codebaseId: codebaseId,
          _splat: codebaseName,
        },
        search: tabSearch,
      },
      {
        key: 'autodocs',
        displayName: 'Autodocs',
        to: '/$organizationName/cb/$codebaseId/autodocs' as const,
        params: {
          organizationName: organizationName,
          codebaseId: codebaseId,
        },
        search: tabSearch,
      },
      // The Atlas tab is gated behind the SERVE_ATLAS flag (default-off → hidden).
      ...(isServeAtlasEnabled
        ? [
            {
              key: 'atlas',
              displayName: 'Atlas',
              to: '/$organizationName/cb/$codebaseId/atlas' as const,
              params: {
                organizationName: organizationName,
                codebaseId: codebaseId,
              },
              search: tabSearch,
            },
          ]
        : []),
      {
        key: 'settings',
        displayName: 'Settings',
        to: '/$organizationName/cb/$codebaseId/settings/general' as const,
        params: {
          organizationName: organizationName,
          codebaseId: codebaseId,
        },
        search: tabSearch,
      },
    ];
  }, [codebaseName, codebaseId, organizationName, tabSearch, isBranchesUiEnabled, isServeAtlasEnabled]);

  const tabDisplayName = tabs.find((t) => t.key === tab)?.displayName;

  useEffect(() => {
    if (codebaseName && tabDisplayName) {
      const pageTitle = `${codebaseName} - ${tabDisplayName}`;
      document.title = pageTitle;
    }
  }, [codebaseName, tabDisplayName]);

  useEffect(() => {
    // Pages sources are handled by usePageTuner in `/autodocs/page/$pageVersionNodeId.tsx`
    // This also restores the sources when navigating away from the page and back to the codebase.
    if (!isPage) {
      // Transform the selected version data into a SourceAssetRecord.
      const sourceAssetRecords = selectedVersion ? transformVersionRecordData([selectedVersion]) : [];
      setDocumentSources(sourceAssetRecords);
    }
  }, [selectedVersion, setDocumentSources, isPage]);

  useEffect(() => {
    if (!tab) {
      navigate({
        to: '/$organizationName/cb/$codebaseId/overview/$',
        params: {
          organizationName: organizationName,
          codebaseId: codebaseId,
        },
        search: (prev) => ({
          ...prev,
        }),
      });
    }
  }, [navigate, tab, organizationName, codebaseId]);

  if (!isPrimaryAssetLoading && !codebase) {
    return <NotFound404 />;
  }

  if (tab === 'code') {
    return <Outlet />;
  }

  return (
    <div className="flex flex-col">
      <LayoutDefault
        navbarTitle={codebase?.display_name}
        header={
          <LayoutHeader right={<CodebaseHeaderActions />}>
            <BreadcrumbsCodebase
              codebaseId={codebaseId}
              path={tab === 'tree' && pathArray.length > 0 ? pathArray : [codebaseName || '']}
              tab={tab}
            />
          </LayoutHeader>
        }
      >
        <LeftSidebarPortal.Provider>
          {tab === 'tree' ? (
            <div className="lg:hidden">
              <LeftSidebarPortal.Slot />
            </div>
          ) : null}
          <FlexiblePanelLayout
            showBorder
            pageType="codebase"
            className={isLargeScreen ? 'h-[calc(100vh-44px)]' : 'h-[calc(100vh-101px)]'}
            leftPanelContent={<LeftSidebarPortal.Slot />}
            leftPanelContentClassName="px-0"
            leftMainHeader={
              isPrimaryAssetLoading ? (
                <CodebaseHeaderSkeleton />
              ) : (
                <>
                  <CodebaseErrorBanner codebaseId={codebaseId} />
                  <LeftMainHeader selectedTab={tab} tabs={tabs} />
                  {(tab === 'tree' || tab === 'autodocs' || tab === 'atlas') && <CodebaseDocsToolbar />}
                </>
              )
            }
            initialLeftPanelWidth={0}
            // CD: Ideally the individual page/routes would control the state of `hideLeftPanel`
            // but the only way I can think to do it is by adding it to context, and not sure it's worth it (yet).
            // `!pathArray[0]` is checking for the autodocId which would indicate this is the details view and we DO want to show the left panel.
            hideLeftPanel={
              !isLargeScreen ||
              (tab === 'autodocs' && !pathArray[0]) ||
              (tab === 'atlas' && !pathArray[0]) ||
              tab === 'settings' ||
              tab === 'branches'
            }
            mainPanelContent={isPrimaryAssetLoading ? <CodebaseMainSkeleton /> : <Outlet />}
            initialRightPanelWidth={308} // 300px + 8px margin
            showRightPanelHeaderDivider={false}
            rightPanelClassName="h-full"
            rightPanelContent={
              <CodebaseRightSidebar
                codebaseId={codebaseId}
                branchId={hasBranches ? selectedBranchId : undefined}
                versionId={versionId}
                isFile={isFile}
                isPage={isPage}
                sourceVersionNodeIds={chatSourceVersionNodeIds}
              />
            }
            rightPanelMinWidthClosed={56} // 48px + 8px margin
            hideExpandCollapseButton
          />
        </LeftSidebarPortal.Provider>
        {openUserDefinedScope && !isPage && (
          <UserDefinedScope
            open={openUserDefinedScope}
            onOpen={setOpenUserDefinedScope}
            onSourcesChanged={setDocumentSources}
            sources={documentSources}
          />
        )}
      </LayoutDefault>
      <BranchSheetFromParams
        branch={branch}
        branchCodebase={branchCodebase ?? codebaseId}
        currentCodebaseId={codebaseId}
        onClose={() =>
          navigate({ to: '.', search: (prev) => ({ ...prev, branch: undefined, branchCodebase: undefined }) })
        }
      />
    </div>
  );
};

function ProvidedCodebaseLayout() {
  const { codebaseId, pageVersionNodeId } = useCodebaseRoute();
  const query = Route.useSearch();
  const { branchId, versionId } = query;

  return (
    <PageProvider pageVersionNodeId={pageVersionNodeId}>
      <MemoryTunerProvider>
        <VersionsProvider sourceId={codebaseId} branchId={branchId} versionId={versionId}>
          <VersionGuardProvider versionId={versionId}>
            <CodebaseProvider>
              <ChatProvider>
                <TabProvider<CodebaseRightSidebarTabType> defaultTab="chat">
                  <CodebaseLayout />
                </TabProvider>
              </ChatProvider>
            </CodebaseProvider>
          </VersionGuardProvider>
        </VersionsProvider>
      </MemoryTunerProvider>
    </PageProvider>
  );
}
